import { computed } from "vue";
import { useAsyncData } from "nuxt/app";
import customFetch from "./customFetch";
import type { ICompanyInfo, ISocialMedias } from "../types/general";

/**
 * Footer social links from `api/company-info`, as the `{ [platform]: url }`
 * object the footers expect. Backend values win per platform; platforms the
 * backend doesn't provide keep the config `socialLinks` value, so a partial
 * backend response never drops an existing link.
 *
 * `social_medias` is normally `[{ platform, url }]`, but some clients get an
 * object of `{ [platform]: { platform: null, url: null } }` — handle both and
 * ignore entries without a url.
 *
 * Uses the same async-data key/fetcher as the other company-info consumers,
 * so it never triggers an extra request.
 */
export default async function useFooterSocialMedias(
  fallback?: Partial<ISocialMedias> | null,
) {
  const { locale } = useI18n();
  const { data } = await useAsyncData<ICompanyInfo | null>(
    `about-${locale.value}`,
    () => customFetch(`api/company-info?lang=${locale.value}`),
  );

  return computed<Partial<ISocialMedias>>(() => {
    const raw: unknown = data.value?.social_medias;
    const items = (
      Array.isArray(raw)
        ? raw
        : raw && typeof raw === "object"
          ? Object.values(raw)
          : []
    ) as Partial<{ platform: string | null; url: string | null }>[];

    const fromBackend: Partial<ISocialMedias> = {};
    for (const item of items) {
      if (!item?.url || !item.platform) continue;
      const key = item.platform.toLowerCase().replace(/[\W_]+/g, "");
      if (!key) continue;
      fromBackend[(key === "x" ? "twitter" : key) as keyof ISocialMedias] =
        item.url;
    }
    return { ...(fallback ?? {}), ...fromBackend };
  });
}
